import { useState } from 'react';
import { Loader2, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent } from '../../../../components/ui/card';
import { Textarea } from '../../../../components/ui/textarea';
import {
    AmendmentRequest,
    PurchaseOrderComment,
    PurchaseOrderDetail,
} from '../../../../types/procurement.types';
import { ApiError } from '../../../../utils/api';
import { purchaseOrdersApi } from '../../../../utils/procurement-api';
import { formatDateTime } from '../procurement-ui';
import { AmendmentChip } from './PurchaseOrderStatus';

/**
 * Comments, and the amendment requests built on top of them.
 *
 * The Yard Manager who spots that a PO is wrong holds no update grant, so there is no edit
 * screen to open: "Request amendment" is a comment that raises a flag Accounts Payable has to
 * answer. Open requests sit at the top in amber with a count; answering one writes a note and
 * leaves the request on the record — resolved is answered, not deleted.
 */

interface Props {
    order: PurchaseOrderDetail;
    /** Re-read the order after a write — the panel owns none of the record state. */
    onChanged: () => void | Promise<void>;
}

const serverMessage = (err: unknown, fallback: string) => {
    if (err instanceof ApiError) {
        // Error bodies are `{ message }` — see the backend route conventions.
        const body = err.data as { message?: string } | null;
        return body?.message || err.message || fallback;
    }
    return err instanceof Error ? err.message || fallback : fallback;
};

export default function AmendmentPanel({ order, onChanged }: Props) {
    const [body, setBody] = useState('');
    const [busy, setBusy] = useState<string | null>(null);
    /** The open request currently being answered, plus the note being written. */
    const [answeringId, setAnsweringId] = useState<number | null>(null);
    const [note, setNote] = useState('');

    const open = order.amendments.filter((amendment) => amendment.status === 'open');
    const answered = order.amendments.filter((amendment) => amendment.status !== 'open');

    const feed: Array<
        | { kind: 'comment'; at: string; comment: PurchaseOrderComment }
        | { kind: 'amendment'; at: string; amendment: AmendmentRequest }
    > = [
        ...order.comments.map((comment) => ({
            kind: 'comment' as const, at: comment.createdAt, comment,
        })),
        ...answered.map((amendment) => ({
            kind: 'amendment' as const, at: amendment.createdAt, amendment,
        })),
    ].sort((a, b) => b.at.localeCompare(a.at));

    const run = async (key: string, write: () => Promise<unknown>, message: string) => {
        try {
            setBusy(key);
            await write();
            toast.success(message);
            setBody('');
            setNote('');
            setAnsweringId(null);
            await onChanged();
        } catch (err) {
            toast.error(serverMessage(err, 'Something went wrong'));
        } finally {
            setBusy(null);
        }
    };

    return (
        <Card>
            <CardContent className="p-4 md:p-6 space-y-5">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-base font-semibold">Comments &amp; amendments</h2>
                        <p className="text-xs text-muted-foreground mt-1">
                            An amendment request is how anyone who can see this PO asks Accounts
                            Payable for a change — no permission to edit the order is needed.
                        </p>
                    </div>
                    <AmendmentChip count={open.length} />
                </div>

                {open.length > 0 && (
                    <div className="space-y-3">
                        {open.map((amendment) => (
                            <div key={amendment.id}
                                 className="rounded-lg border border-amber-300 bg-amber-50/70 p-3 space-y-2">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-xs font-medium text-amber-900">
                                        {amendment.requestedBy.name} · {formatDateTime(amendment.createdAt)}
                                    </p>
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                                        Amendment requested
                                    </span>
                                </div>
                                <p className="text-sm text-amber-900 whitespace-pre-wrap">{amendment.body}</p>

                                {!order.abilities.canUpdate ? (
                                    <p className="text-xs text-amber-800">
                                        Waiting on Accounts Payable to answer.
                                    </p>
                                ) : answeringId === amendment.id ? (
                                    <div className="space-y-2">
                                        <Textarea
                                            rows={2}
                                            value={note}
                                            autoFocus
                                            placeholder="What was decided — this note is the answer and stays on the record."
                                            onChange={(e) => setNote(e.target.value)}
                                        />
                                        <p className="text-xs text-amber-800">
                                            {note.trim().length} characters — a note is required.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            <Button
                                                size="sm"
                                                disabled={!note.trim() || busy !== null}
                                                onClick={() => run(
                                                    `resolve:${amendment.id}`,
                                                    () => purchaseOrdersApi.resolveAmendment(
                                                        order.id, amendment.id, note.trim()),
                                                    'Amendment resolved — the answer is on the record')}
                                            >
                                                {busy === `resolve:${amendment.id}`
                                                    && <Loader2 size={14} className="animate-spin mr-1.5" />}
                                                Resolve — answered
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                disabled={!note.trim() || busy !== null}
                                                onClick={() => run(
                                                    `dismiss:${amendment.id}`,
                                                    () => purchaseOrdersApi.dismissAmendment(
                                                        order.id, amendment.id, note.trim()),
                                                    'Amendment dismissed — the answer is on the record')}
                                            >
                                                {busy === `dismiss:${amendment.id}`
                                                    && <Loader2 size={14} className="animate-spin mr-1.5" />}
                                                Dismiss
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => { setAnsweringId(null); setNote(''); }}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        <Button size="sm" variant="outline"
                                                onClick={() => { setAnsweringId(amendment.id); setNote(''); }}>
                                            Resolve…
                                        </Button>
                                        <Button size="sm" variant="outline"
                                                onClick={() => { setAnsweringId(amendment.id); setNote(''); }}>
                                            Dismiss…
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {order.abilities.canComment && (
                    <div className="space-y-2">
                        <Textarea
                            rows={3}
                            value={body}
                            placeholder="Add a comment, or describe what needs changing on this PO."
                            onChange={(e) => setBody(e.target.value)}
                        />
                        <div className="flex flex-wrap gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={!body.trim() || busy !== null}
                                onClick={() => run(
                                    'comment',
                                    () => purchaseOrdersApi.comment(order.id, body.trim()),
                                    'Comment added')}
                            >
                                {busy === 'comment' && <Loader2 size={14} className="animate-spin mr-1.5" />}
                                <MessageSquare size={14} className="mr-1.5" />Add comment
                            </Button>
                            <Button
                                size="sm"
                                className="bg-amber-600 text-white hover:bg-amber-700"
                                disabled={!body.trim() || busy !== null}
                                onClick={() => run(
                                    'amendment',
                                    () => purchaseOrdersApi.requestAmendment(order.id, body.trim()),
                                    'Amendment requested — Accounts Payable will see it')}
                            >
                                {busy === 'amendment' && <Loader2 size={14} className="animate-spin mr-1.5" />}
                                <Send size={14} className="mr-1.5" />Request amendment
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Requesting an amendment flags this PO for Accounts Payable. Use it when
                            you can see the order is wrong but cannot change it yourself.
                        </p>
                    </div>
                )}

                <div className="space-y-3">
                    <h3 className="text-sm font-semibold">Conversation</h3>
                    {feed.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            Nothing said yet. Comments and answered amendments both appear here.
                        </p>
                    ) : feed.map((item) => item.kind === 'comment' ? (
                        <div key={`comment-${item.comment.id}`}
                             className="rounded-lg border border-border p-3">
                            <p className="text-xs text-muted-foreground">
                                {item.comment.user.name} · {formatDateTime(item.comment.createdAt)}
                            </p>
                            <p className="text-sm mt-1 whitespace-pre-wrap">{item.comment.body}</p>
                        </div>
                    ) : (
                        <div key={`amendment-${item.amendment.id}`}
                             className="rounded-lg border border-border bg-muted/30 p-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    {item.amendment.status === 'resolved'
                                        ? 'Amendment resolved'
                                        : 'Amendment dismissed'}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                    {item.amendment.requestedBy.name} · {formatDateTime(item.amendment.createdAt)}
                                </span>
                            </div>
                            <p className="text-sm mt-1 whitespace-pre-wrap">{item.amendment.body}</p>
                            <div className="mt-2 border-t border-border pt-2">
                                <p className="text-xs font-medium">
                                    {item.amendment.status === 'resolved'
                                        ? 'Answered'
                                        : 'Answered — no change made'}
                                    {item.amendment.resolvedBy ? ` by ${item.amendment.resolvedBy.name}` : ''}
                                    {item.amendment.resolvedAt
                                        ? ` · ${formatDateTime(item.amendment.resolvedAt)}`
                                        : ''}
                                </p>
                                <p className="text-sm mt-1 whitespace-pre-wrap">
                                    {item.amendment.resolutionNote}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Kept on the record — answering an amendment never deletes it.
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}
